/**
 * EFFET : webcam + MediaPipe Hand Landmarker → frames de la main, pour l'engagement
 * par geste. Ne tourne que pendant la phase d'engagement (3e modèle : on l'économise) ;
 * `stop` garde le modèle chargé (worker au repos) pour repartir sans délai. Une analyse
 * sans réponse (worker gelé) : MediaPipe rechargé.
 */
import { TimeoutError, withTimeout } from '../lib/timeout';
import { onCameraFrames, readPixels, type CameraFrames, type CameraImage } from './cameraFrames';
import { preferredDelegate } from './delegatePreference';
import { createDetector, type Detector } from './detector';
import { DETECT_TIMEOUT_MS } from './faceTracker';
import { toHandFrame } from './mediapipeMapping';
import type { HandSource } from './syntheticHand';
import type { HandFrame } from './types';

const MAX_FPS = 15;

export function createCameraHandSource(video: HTMLVideoElement): HandSource {
  const listeners = new Set<(frame: HandFrame) => void>();
  let detector: Detector<'hand'> | undefined;
  /** Un seul chargement du modèle, même si `start` est rappelé pendant qu'il charge. */
  let loading: Promise<Detector<'hand'>> | undefined;
  let loop: CameraFrames | undefined;
  let lastAnalysis = -Infinity;
  let busy = false;
  /** Incrémenté à chaque start/stop : un chargement du modèle périmé ne démarre rien. */
  let generation = 0;

  const load = (): Promise<Detector<'hand'>> =>
    (loading ??= createDetector('hand', preferredDelegate()).catch((e: unknown) => {
      // Échec du chargement : oublié, pour qu'un prochain `start` réessaie.
      loading = undefined;
      throw e;
    }));

  const rebuild = (cause: unknown): void => {
    console.warn('MediaPipe (main) rechargé :', cause);
    detector?.close();
    detector = undefined;
    loading = undefined;
    void load().then(
      (fresh) => {
        if (loop) detector = fresh;
        else fresh.close();
      },
      () => undefined,
    );
  };

  const onFrame = (image: CameraImage, t: number): void => {
    const current = detector;
    if (!current || busy || t - lastAnalysis < 1000 / MAX_FPS - 2) return;
    const data = readPixels(image);
    if (!data) return;
    lastAnalysis = t;
    busy = true;
    withTimeout(
      current.detect(data.data.buffer, data.width, data.height, t),
      DETECT_TIMEOUT_MS,
      'analyse de la main',
    ).then(
      ({ result }) => {
        busy = false;
        if (detector !== current) return;
        const frame = toHandFrame(t, result);
        for (const l of listeners) l(frame);
      },
      (e: unknown) => {
        busy = false;
        if (detector !== current) return;
        if (e instanceof TimeoutError) rebuild(e);
        // Sinon : image ignorée, la boucle continue (l'engagement a un bouton de secours).
        else console.warn('Analyse de la main impossible :', e);
      },
    );
  };

  return {
    async start() {
      const current = ++generation;
      detector ??= await load();
      // Arrêté pendant le chargement du modèle : on ne démarre pas la boucle.
      if (current === generation && !loop) loop = onCameraFrames(video, onFrame);
    },
    stop() {
      generation++;
      loop?.stop();
      loop = undefined;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
